import React, { ReactNode, useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/ui/date-picker";
import { Textarea } from "@/components/ui/textarea";

import {
  adminAddCandidateToJobFormSchema,
  AdminAddCandidateToJobFormSchema,
} from "@/components/forms/admin/job-candidate/candidate-status/schema";
import { useCreateCandidateStatus } from "@/mutations/admin/create-candidate-status";
import { useUpdateCandidateStatus } from "@/mutations/admin/update-candidate-status";

import {
  AdminJobCandidateDetails,
  AdminJobDetails,
} from "@/queries/admin/job-details";
import { budgetTypeList } from "@/types";

const CandidateStatusForm = ({
  jobOpening,
  candidateOptions,
  matchCriteria,
  candidate,
  onSuccess,
}: {
  jobOpening: AdminJobDetails;
  candidateOptions: {
    id: string;
    name: string;
    disabled: boolean | undefined;
  }[];
  matchCriteria: string[];
  candidate?: AdminJobCandidateDetails;
  onSuccess?: () => void;
}) => {
  const {
    createCandidateOpportunityStatus,
    isCreatingCandidateOpportunityStatus,
  } = useCreateCandidateStatus(jobOpening.id, onSuccess);
  const {
    updateCandidateOpportunityStatus,
    isUpdatingCandidateOpportunityStatus,
  } = useUpdateCandidateStatus(jobOpening.id, onSuccess);

  const defaultValues = useMemo(
    () => ({
      candidate: candidate?.candidateId,
      agreesWithScope: candidate?.agreesWithScope ?? false,
      agreesWithBudget: candidate?.agreesWithBudget ?? false,
      agreesWithStartDate: candidate?.agreesWithStartDate ?? false,
      scope: candidate?.scope ?? "",
      budgetType: candidate?.budgetType ?? undefined,
      budgetHourly: candidate?.budgetHourly ?? "",
      budgetMonthly: candidate?.budgetMonthly ?? "",
      startDate: candidate?.startDate
        ? new Date(candidate?.startDate)
        : undefined,
      matchCriteria: candidate?.matchCriteria.length
        ? candidate.matchCriteria
        : ["", "", ""],
      matchCriteriaQuote: candidate?.matchCriteriaQuote.length
        ? candidate.matchCriteriaQuote
        : ["", "", ""],
      personalityRoleStrength: candidate?.personalityRoleStrength ?? "",
      personalityRoleStrengthDetail:
        candidate?.personalityRoleStrengthDetail ?? "",
      behaviorRoleStrength: candidate?.behaviorRoleStrength ?? "",
      behaviorRoleStrengthDetail: candidate?.behaviorRoleStrengthDetail ?? "",
      motivatorRoleStrength: candidate?.motivatorRoleStrength ?? "",
      motivatorRoleStrengthDetail: candidate?.motivatorRoleStrengthDetail ?? "",
    }),
    [candidate],
  );

  const form = useForm<AdminAddCandidateToJobFormSchema>({
    resolver: async (data, context, opts) => {
      return zodResolver(adminAddCandidateToJobFormSchema)(data, context, opts);
    },
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues]);

  const mode = useMemo(() => (candidate ? "update" : "create"), [candidate]);

  const onSubmit = (data: AdminAddCandidateToJobFormSchema): void => {
    if (mode === "create") {
      createCandidateOpportunityStatus(data);
    } else {
      updateCandidateOpportunityStatus(data);
    }
  };

  return (
    <Form {...form}>
      <form className="space-y-4 w-full">
        {mode === "create" ? (
          <FormField
            control={form.control}
            name="candidate"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Candidate</FormLabel>
                <FormControl>
                  <Select
                    name={field.name}
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={field.disabled}
                  >
                    <SelectTrigger className="w-full max-w-[250px]">
                      <SelectValue placeholder="Select a candidate" />
                    </SelectTrigger>
                    <SelectContent>
                      {candidateOptions.map((candidate) => (
                        <SelectItem
                          key={`candidate-${candidate.id}`}
                          value={candidate.id}
                          disabled={candidate.disabled}
                        >
                          {candidate.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        ) : (
          <FormItem>
            <FormLabel>Candidate</FormLabel>
            {candidate?.name}
            <FormMessage />
          </FormItem>
        )}
        <TwoColumnWrapper>
          <FormField
            control={form.control}
            name="agreesWithScope"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Agrees with scope</FormLabel>
                <div className="flex-1 flex items-center">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="scope"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Scope</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="2 days a week / 8 months" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </TwoColumnWrapper>
        <FormField
          control={form.control}
          name="agreesWithBudget"
          render={({ field }) => (
            <FormItem className="flex flex-col">
              <FormLabel>Agrees with budget</FormLabel>
              <div className="flex-1 flex items-center">
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                </FormControl>
              </div>
              <FormMessage />
            </FormItem>
          )}
        />
        <TwoColumnWrapper>
          <FormField
            control={form.control}
            name="budgetHourly"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Budget (hourly)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="$100/hr" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="budgetMonthly"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Budget (monthly retainer)</FormLabel>
                <FormControl>
                  <Input {...field} placeholder="$4,000/month" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </TwoColumnWrapper>

        {jobOpening.type === "Project" && (
          <FormField
            control={form.control}
            name="budgetType"
            render={({ field }) => (
              <FormItem>
                <FormLabel required>Budget Type</FormLabel>
                <FormControl>
                  <Select
                    name={field.name}
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={field.disabled}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={"Select budget type"} />
                    </SelectTrigger>
                    <SelectContent>
                      {budgetTypeList.map((role) => (
                        <SelectItem key={`budget-type-${role}`} value={role}>
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}

        <TwoColumnWrapper>
          <FormField
            control={form.control}
            name="agreesWithStartDate"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Agrees with start date</FormLabel>
                <div className="flex-1 flex items-center">
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="startDate"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Earliest start date</FormLabel>
                <FormControl>
                  <DatePicker
                    value={field.value}
                    onChange={field.onChange}
                    modal
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </TwoColumnWrapper>
        <TwoColumnWrapper>
          <FormField
            control={form.control}
            name="personalityRoleStrength"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Personality Strength</FormLabel>
                <div className="flex-1 flex items-center">
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Strategic Financial Leadership"
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="personalityRoleStrengthDetail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Personality Details</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Will excel in fostering a collaborative environment, which is essential for aligning financial strategies with your innovative and patient-centric values."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </TwoColumnWrapper>
        <TwoColumnWrapper>
          <FormField
            control={form.control}
            name="behaviorRoleStrength"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Behavior Strength</FormLabel>
                <div className="flex-1 flex items-center">
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Budgeting and Forecasting Acumen"
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="behaviorRoleStrengthDetail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Behavior Details</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Effective in engaging with department heads to develop budgets that reflect the company's strategic objectives and foster a team-oriented approach."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </TwoColumnWrapper>
        <TwoColumnWrapper>
          <FormField
            control={form.control}
            name="motivatorRoleStrength"
            render={({ field }) => (
              <FormItem className="flex flex-col">
                <FormLabel>Motivator Strength</FormLabel>
                <div className="flex-1 flex items-center">
                  <FormControl>
                    <Textarea
                      {...field}
                      placeholder="Incentive Plan Architect"
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="motivatorRoleStrengthDetail"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Motivator Details</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    placeholder="Indicates a strong potential for creating sales compensation plans that not only motivate the team but also align with your growth and revenue goals."
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </TwoColumnWrapper>
        {matchCriteria.some((criteria) => Boolean(criteria)) && (
          <>
            <FormLabel>Match Criteria</FormLabel>
            {matchCriteria.map((criteria, idx) => (
              <React.Fragment key={`match-criteria-wrapper-${idx}`}>
                <FormField
                  key={`match-criteria-${idx}`}
                  control={form.control}
                  name={`matchCriteria.${idx}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{criteria}</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Details" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  key={`match-criteria-quote-${idx}`}
                  control={form.control}
                  name={`matchCriteriaQuote.${idx}`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Quote</FormLabel>
                      <FormControl>
                        <Textarea {...field} placeholder="Quote" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {matchCriteria.length - 1 !== idx && (
                  <div className="h-[1px] bg-[#E4E7EC] w-full my-4" />
                )}
              </React.Fragment>
            ))}
          </>
        )}

        <div className="w-full flex justify-end">
          <Button
            variant="default"
            className="w-fit"
            disabled={
              !form.formState.isValid ||
              isCreatingCandidateOpportunityStatus ||
              isUpdatingCandidateOpportunityStatus
            }
            onClick={(ev) => {
              ev.preventDefault();
              ev.stopPropagation();
              void form.handleSubmit(onSubmit)();
            }}
          >
            {mode === "create" ? "Add Candidate" : "Update Candidate"}
          </Button>
        </div>
      </form>
    </Form>
  );
};

const TwoColumnWrapper = (props: { children: ReactNode }) => {
  return (
    <div className="grid gap-4 grid-cols-[repeat(auto-fit,minmax(300px,1fr))]">
      {props.children}
    </div>
  );
};

export default CandidateStatusForm;
